import type { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import qs from "qs"
import { head, omit } from "ramda"
import { defer, EMPTY, merge, Observable, of, timer } from "rxjs"
import { catchError, delay, filter, finalize, map, switchMap, take, tap } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import { createHttpRequest } from "../../../api/utils/httpRequest"
import { EventType } from "../../../events/types"
import { createEventRequest, requestEvent } from "../../../events/utils"
import type {
    LocationDetail,
    ServiceDetail,
    TerminalAddRequestData,
    TerminalAddResponseData,
} from "../../../graphql/types"
import { ServiceMetadata, TerminalType, TerminalUpdateRequestData } from "../../../graphql/types"
import { Router } from "../../../server/router"
import { Config } from "../../../utils/config"
import {
    ACCOUNT_SELECTED,
    AGENDA_MOUNTED,
    AUTH_LOCATIONS_REFRESHED,
    authLocationsRefreshRequested,
    REFETCH_DATA,
} from "../../auth/actions"
import {
    getHttpHeaders,
    getLocationDataByTerminalId,
    getLocationsAuthData,
    getSelectedAccountId,
} from "../../auth/selectors"
import { locationOverrideSelected, locationSelected } from "../../common/locationsPicker/actions"
import { getSelectedLocationId, getSelectedLocationOverrideId } from "../../common/locationsPicker/selectors"
import {
    LOCATION_CREATED,
    LOCATION_DELETED,
    LOCATION_UPDATED,
    locationCreated,
    locationDeleted,
    locationUpdated,
    TERMINAL_CREATED,
    TERMINAL_UPDATED,
    terminalCreated,
    terminalUpdated,
} from "../../data/actions"
import { isLocationsAndTerminalsAgendaOpened } from "../../data/selectors"
import { sendOperationsClearState } from "../../operations/actions"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import {
    closeLoadingNotification,
    NotificationType,
    openLoadingNotification,
    openNotification,
} from "../../uiKit/Notification/Notification"
import { downloadReportFailure } from "../reports/actions"
import {
    AGGREGATOR_CREATE_FORM_SUBMITTED,
    AGGREGATOR_EDIT_FORM_SUBMITTED,
    AGGREGATOR_FOODMEOW_STORE_SELECTED,
    AGGREGATOR_FOODMEOW_STORES_REQUESTED,
    AGGREGATOR_INTEGRATE_FORM_SUBMITTED,
    AGGREGATOR_INTEGRATION_REQUESTED,
    AGGREGATOR_SERVICE_DETAIL_FORM_AGENDA_MOUNTED,
    AGGREGATOR_UBER_STORE_SELECTED,
    AGGREGATOR_UBER_STORES_REQUESTED,
    aggregatorFoodmeowStoreSelected,
    aggregatorFoodmeowStoresError,
    aggregatorFoodmeowStoresSuccess,
    aggregatorIntegrationRequested,
    aggregatorUberStoreSelected,
    aggregatorUberStoresError,
    aggregatorUberStoresSuccess,
    CLOSE_AGGREGATOR_FORM_CLICKED,
    CLOSE_TERMINAL_FORM_CLICKED,
    DELETE_AGGREGATOR_CLICKED,
    DELETE_LOCATION_CLICKED,
    DELETE_TERMINAL_CLICKED,
    deleteTerminalClicked,
    fetchLocationDetailError,
    fetchLocationDetailSuccess,
    fetchLocationsFailure,
    fetchLocationsListError,
    fetchLocationsListSuccess,
    fetchLocationsSuccess,
    fetchServiceDetailError,
    fetchServiceDetailSuccess,
    INTEGRATE_AGGREGATOR_SUCCESS,
    integrateAggregatorSuccess,
    LOCATION_AGENDA_MOUNTED,
    LOCATION_DETAIL_AGENDA_MOUNTED,
    LOCATION_FORM_SUBMITTED,
    SAVE_LOCATION_SUCCESS,
    SAVE_TERMINAL_SUCCESS,
    saveLocationFailure,
    saveLocationSuccess,
    saveTerminalFailure,
    saveTerminalSuccess,
    SERVICE_INTEGRATE_3RD_PARTY_REQUESTED,
} from "./actions"
import { GOOGLE_REVIEWS_AGENDA_MOUNTED } from "../reports/customers/actions"
import { AggregatorTypeEnum } from "./aggregatorForm/aggregatorTypeEnum"
import type { AggregatorCreateFormFields } from "./aggregatorForm/Create"
import type { AggregatorIntegrateFormData } from "./aggregatorForm/Integrate"
import {
    fetchFoodmeowStoresQuery,
    fetchLocationDetailData,
    fetchLocationsList,
    fetchLocationsTerminals,
    fetchServiceDetailData,
    fetchUberStoresQuery,
    fetchUberStoresQueryByCode,
    generateAggregatorEmail,
    linkFoodmeowStore,
    linkUberStore,
    postLocationDeleteMutation,
    postLocationSaveMutation,
    postTerminalAddMutation,
    postTerminalUpdateMutation,
    removeIntegrationMutation,
    terminalDeleteMutation,
} from "./request"
import {
    getEditLocationId,
    getEditServiceListItem,
    getEditTerminalId,
    getServiceDetailData,
    shouldDownloadLocationsTerminalsData,
} from "./selectors"
import { TerminalDTO } from "./types"
import { constructPriceMultiplierValue } from "./utils"
import { logger } from "../../../utils/loggerWrap"
import { Action, StateObject } from "../../redux/types"
import { constructAuditData } from "../auditLog/utils/utils-payload-data"

export const fetchLocationsRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(
            LOCATION_AGENDA_MOUNTED,
            ACCOUNT_SELECTED,
            REFETCH_DATA,
            AGENDA_MOUNTED,
            GOOGLE_REVIEWS_AGENDA_MOUNTED,
            AUTH_LOCATIONS_REFRESHED
        ),
        filter(() => shouldDownloadLocationsTerminalsData(deps.getState())),
        switchMap(() =>
            defer(() => fetchLocationsTerminals(getHttpHeaders(deps.getState()))).pipe(
                switchMap(response => [fetchLocationsSuccess(response)]),
                catchError(error => of(fetchLocationsFailure(error)))
            )
        )
    )

export const fetchLocationsListRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(LOCATION_AGENDA_MOUNTED)),
        actions$.pipe(
            ofType(ACCOUNT_SELECTED, REFETCH_DATA, AUTH_LOCATIONS_REFRESHED),
            filter(() => isLocationsAndTerminalsAgendaOpened(deps.getState()))
        )
    ).pipe(
        switchMap(() =>
            defer(() => fetchLocationsList(getHttpHeaders(deps.getState()))).pipe(
                switchMap(response => [fetchLocationsListSuccess(response)]),
                catchError(error => of(fetchLocationsListError(error)))
            )
        )
    )

export const fetchLocationsDetailRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_DETAIL_AGENDA_MOUNTED),
        switchMap(() => {
            const locationId = getEditLocationId(deps.getState())

            if (!locationId) {
                return EMPTY
            }

            return defer(() => fetchLocationDetailData(getHttpHeaders(deps.getState()), locationId)).pipe(
                switchMap(response => [fetchLocationDetailSuccess(response)]),
                catchError(error => of(fetchLocationDetailError(error)))
            )
        })
    )

export const fetchAggregatorServiceDetailRequested = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_SERVICE_DETAIL_FORM_AGENDA_MOUNTED),
        switchMap(() => {
            const serviceId = getEditTerminalId(deps.getState())

            if (!serviceId) {
                return EMPTY
            }

            return defer(() => fetchServiceDetailData(getHttpHeaders(deps.getState()), serviceId)).pipe(
                switchMap(response => [fetchServiceDetailSuccess(response)]),
                catchError(error => of(fetchServiceDetailError(error)))
            )
        })
    )

export const locationFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("locationFormSubmittedEpics - form submitted", {
                data: action.payload,
            })

            const oldValue: Maybe<LocationDetail> = action.payload.oldValue
            const newValue: LocationDetail = action.payload.newValue

            if (!oldValue) {
                return [locationCreated({ ...newValue, id: deps.idgen.next() })]
            }

            return [locationUpdated(newValue)]
        })
    )

export const locationCreatedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_CREATED),
        switchMap((action: Action) => {
            logger.info("locationCreatedEpics - form submitted", {
                data: action.payload,
            })

            const auditData = constructAuditData(deps, true)

            const data = action.payload.location
            return defer(() =>
                postLocationSaveMutation(
                    getHttpHeaders(deps.getState()),
                    // @ts-expect-error
                    omit(["terminals", "googleBusinessProfile"], { ...data, auditData: auditData })
                )
            ).pipe(
                switchMap(() => [saveLocationSuccess(), authLocationsRefreshRequested(data.id)]),
                catchError(error => of(saveLocationFailure(error)))
            )
        })
    )

export const locationUpdatedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_UPDATED),
        switchMap((action: Action) => {
            logger.info("locationUpdatedEpics - form submitted", {
                data: action.payload,
            })

            const auditData = constructAuditData(deps, false)

            const data = action.payload.newValue
            return defer(() =>
                postLocationSaveMutation(
                    getHttpHeaders(deps.getState()),
                    // @ts-expect-error
                    omit(["terminals", "googleBusinessProfile"], { ...data, auditData: auditData })
                )
            ).pipe(
                switchMap(() => [saveLocationSuccess()]),
                catchError(error => of(saveLocationFailure(error)))
            )
        })
    )

export const deleteLocationClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_LOCATION_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteLocationClickedEpics - form submitted", {
                data: action.payload,
            })

            return [locationDeleted(action.payload.locationId)]
        }),
        catchError(err => of(saveLocationFailure(err)))
    )

export const deleteLocationEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOCATION_DELETED),
        switchMap((action: Action) => {
            logger.info("deleteLocationEpics - form submitted", {
                data: action.payload,
            })

            return defer(() =>
                postLocationDeleteMutation(getHttpHeaders(deps.getState()), action.payload.locationId)
            ).pipe(
                switchMap(() => {
                    const actions = [saveLocationSuccess()]

                    // If the deleted location was selected, select the first location
                    const firstLocation = head(getLocationsAuthData(deps.getState()))
                    if (getSelectedLocationOverrideId(deps.getState()) === action.payload.locationId) {
                        firstLocation && actions.push(locationOverrideSelected(firstLocation?.id))
                    }
                    if (getSelectedLocationId(deps.getState()) === action.payload.locationId) {
                        firstLocation && actions.push(locationSelected(firstLocation?.id))
                    }

                    return actions
                }),
                catchError(error => of(saveLocationFailure(error)))
            )
        })
    )
// Terminalis

export const terminalCreatedEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(TERMINAL_CREATED),
        switchMap((action: Action) => {
            logger.info("terminalCreatedEpics - form submitted", {
                data: action.payload,
            })

            return [saveTerminalSuccess()]
        })
    )

export const terminalUpdatedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TERMINAL_UPDATED),
        switchMap((action: Action) => {
            logger.info("terminalUpdatedEpics - terminal updated", {
                data: action.payload,
            })

            if (action.payload.skipRequest) {
                return [saveTerminalSuccess()]
            }

            const data = action.payload.newValue
            const showNofif = action.payload.showNotification
            const auditData = constructAuditData(deps, false)
            const requestData = {
                id: data.id,
                isActive: data.isActive,
                locationId: data.locationId,
                name: data.name,
                type: data.type,
                kioskCustomerInstructions: data.kioskCustomerInstructions,
                aggregatorConfig: data.aggregatorConfig,
                metadata: data.metadata,
                mibUrl: data.url,
                auditData: auditData,
            } as TerminalUpdateRequestData

            showNofif && openLoadingNotification()

            return defer(() => postTerminalUpdateMutation(getHttpHeaders(deps.getState()), requestData)).pipe(
                switchMap(() => {
                    showNofif && openNotification(NotificationType.Success, "Service data saved")
                    return [saveTerminalSuccess()]
                }),
                catchError(error => {
                    showNofif &&
                        openNotification(NotificationType.Error, "Could not save data", "Please try again later")
                    logger.error("terminalFormSubmittedEpics", null, error)
                    return of(saveTerminalFailure(error))
                }),
                finalize(() => {
                    showNofif && closeLoadingNotification()
                })
            )
        })
    )

export const deleteAggregatorClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_AGGREGATOR_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteAggregatorClickedEpics - delete clicked", {
                data: action.payload,
            })

            return defer(() =>
                removeIntegrationMutation(getHttpHeaders(deps.getState()), {
                    storeId: action.payload.aggregatorId,
                    type: action.payload.type,
                })
            ).pipe(
                switchMap(() => [deleteTerminalClicked(action.payload.aggregatorId, action.payload.locationId)]),
                catchError(error => of(saveLocationFailure(error)))
            )
        })
    )

export const deleteTerminalClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_TERMINAL_CLICKED),
        switchMap((action: Action) => {
            logger.info("deleteTerminalClickedEpics - delete clicked", {
                data: action.payload,
            })

            if (action.payload.skipRequest) {
                return []
            }

            return defer(() => terminalDeleteMutation(getHttpHeaders(deps.getState()), action.payload.terminalId)).pipe(
                switchMap(() => []),
                catchError(error => of(saveLocationFailure(error)))
            )
        })
    )

export const closeLocationsPopoverRouterEpic = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(
            CLOSE_TERMINAL_FORM_CLICKED,
            DELETE_TERMINAL_CLICKED,
            CLOSE_AGGREGATOR_FORM_CLICKED,
            AGGREGATOR_UBER_STORE_SELECTED,
            AGGREGATOR_EDIT_FORM_SUBMITTED
        ),
        switchMap(() => {
            Router.push("/locations")
            return EMPTY
        })
    )

export const clearStatusStateEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SAVE_TERMINAL_SUCCESS, SAVE_LOCATION_SUCCESS, INTEGRATE_AGGREGATOR_SUCCESS),
        delay(2000),
        map(() => sendOperationsClearState())
    )

export const onIntegrate3rdPartRequestedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SERVICE_INTEGRATE_3RD_PARTY_REQUESTED),
        switchMap((action: Action) => {
            logger.info("onIntegrate3rdPartRequestedEpic - integrate requested", {
                data: action.payload,
            })

            const payload = action.payload
            const accountId = getSelectedAccountId(deps.getState())

            if (!accountId || !payload.locationId || !payload.serviceId) {
                return EMPTY
            }

            return defer(() =>
                createHttpRequest({
                    method: "POST",
                    path: `/terminal-integration?accountId=${accountId}&locationId=${payload.locationId}&terminalId=${payload.serviceId}&type=${payload.type}`,
                    headers: getHttpHeaders(deps.getState()),
                })
            ).pipe(
                switchMap(() => EMPTY),
                catchError(error => of(downloadReportFailure(error)))
            )
        })
    )

export const aggregatorCreateFormEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_CREATE_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("aggregatorCreateFormEpic - form submitted", {
                data: action.payload,
            })

            const item: AggregatorCreateFormFields = action.payload.formData
            const terminalId = deps.idgen.next()
            const data: TerminalAddRequestData = {
                id: terminalId,
                locationId: item.locationId,
                name: item.name,
                isActive: false,
                type: TerminalType.Aggregator,
            }

            const accountId = getSelectedAccountId(deps.getState())
            if (!accountId) {
                return EMPTY
            }

            if (item.aggregatorType === AggregatorTypeEnum.Grubhub) {
                return defer(() =>
                    requestEvent(
                        getHttpHeaders(deps.getState()),
                        createEventRequest(
                            EventType.OnlineOrderingIntegrationRequested,
                            {
                                accountId: accountId,
                                locationId: item.locationId,
                                correlationId: deps.idgen.next(),
                            },
                            {
                                service: item.aggregatorType,
                                name: item.name,
                                externalId: item.merchantId,
                            }
                        )
                    )
                ).pipe(
                    switchMap(response => {
                        if (!response || !response.data) {
                            return EMPTY
                        }

                        const serviceDetail: ServiceDetail = mapTerminalDTOtoServiceDetail(response.data.terminal)
                        Router.push("/locations/edit/aggregator/" + serviceDetail.id)
                        return [
                            terminalCreated(serviceDetail),
                            fetchServiceDetailSuccess(serviceDetail),
                            integrateAggregatorSuccess(item.locationId, terminalId),
                        ]
                    })
                )
            }

            return defer(() => postTerminalAddMutation(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap((response: TerminalAddResponseData) => {
                    return [
                        terminalCreated({
                            ...data,
                            // @ts-expect-error
                            status: response.status,
                            prefix: response.terminalPrefix,
                            slug: response.slug,
                        }),
                        aggregatorIntegrationRequested(item.locationId, terminalId, item.aggregatorType, item.name),
                    ]
                })
            )
        })
    )

export const mapTerminalDTOtoServiceDetail = (terminal: TerminalDTO): ServiceDetail => {
    return {
        id: terminal.id,
        locationId: terminal.locationId,
        name: terminal.name,
        type: terminal.type,
        isActive: terminal.isActive,
        url: terminal.slug,
        metadata: terminal.metadata as Maybe<ServiceMetadata>,
    }
}

export const aggregatorIntegrateFormEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_INTEGRATE_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("aggregatorIntegrateFormEpic - form submitted", {
                data: action.payload,
            })

            const item: AggregatorIntegrateFormData = action.payload
            const auditData = constructAuditData(deps, true)
            const data = {
                id: item.id,
                name: item.name,
                locationId: item.locationId,
                isActive: false,
                kioskCustomerInstructions: null,
                type: TerminalType.Aggregator,
                auditData: auditData,
            }
            return defer(() => postTerminalUpdateMutation(getHttpHeaders(deps.getState()), data)).pipe(
                switchMap(() => {
                    return [
                        terminalUpdated(data),
                        aggregatorIntegrationRequested(item.locationId, item.id, item.aggregatorType, item.name),
                    ]
                }),
                catchError(error => of(saveTerminalFailure(error)))
            )
        })
    )

export const aggregatorIntegrationRequestedEpic = (
    actions$: Observable<any>,
    _state$: Observable<any>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_INTEGRATION_REQUESTED),
        switchMap(action => {
            logger.info("aggregatorIntegrationRequestedEpic - requested", {
                data: action.payload,
            })

            const { locationId, terminalId, aggregatorType, terminalName } = action.payload

            const accountId = getSelectedAccountId(deps.getState())

            if (aggregatorType === AggregatorTypeEnum.Uber) {
                const params = qs.stringify({
                    accountId: accountId,
                    locationId: locationId,
                    newIntegrationFlow: 1,
                })
                // redirect to API endpoint which redirects to uber oauth
                window.location.href = `${Config.webApiBaseUrl}/aggregator/${terminalId}/uber-oauth?${params}`
                return EMPTY
            } else if (aggregatorType === AggregatorTypeEnum.Foodmeow) {
                // redirect to API endpoint which redirects to foodmeow oauth
                window.location.href = `${Config.webApiBaseUrl}/aggregator/${terminalId}/foodmeow-oauth`
                return EMPTY
            } else if (
                [
                    AggregatorTypeEnum.Email,
                    AggregatorTypeEnum.DoorDash,
                    AggregatorTypeEnum.Grubhub,
                    AggregatorTypeEnum.Slice,
                ].includes(aggregatorType)
            ) {
                return defer(() =>
                    generateAggregatorEmail(getHttpHeaders(deps.getState()), {
                        terminalId: terminalId,
                        locationId: locationId,
                        aggregatorType: aggregatorType,
                    })
                ).pipe(
                    switchMap(() =>
                        timer(500, 2000).pipe(
                            switchMap(_ => fetchServiceDetailData(getHttpHeaders(deps.getState()), terminalId)),
                            filter(
                                (res: Maybe<ServiceDetail>) =>
                                    !!(
                                        res &&
                                        res.aggregatorConfig &&
                                        (res.aggregatorConfig.email ||
                                            res.aggregatorConfig.grubhub ||
                                            res.aggregatorConfig.doordash ||
                                            res.aggregatorConfig.slice)
                                    )
                            ),
                            take(1),
                            tap(() => {
                                Router.push("/locations/edit/aggregator/" + terminalId)
                            }),
                            switchMap(res => {
                                if (!res) {
                                    // this should never throw because in the lines above we filter falsy values out
                                    throw new Error("Logic error: res is falsy")
                                }

                                const terminalUpdatedData = {
                                    id: res.id,
                                    name: res.name,
                                    locationId: res.locationId,
                                    isActive: true,
                                    kioskCustomerInstructions: null,
                                    type: TerminalType.Aggregator,
                                }
                                return [
                                    fetchServiceDetailSuccess(res),
                                    integrateAggregatorSuccess(locationId, terminalId),
                                    terminalUpdated(terminalUpdatedData),
                                ]
                            })
                        )
                    )
                )
            } else if (aggregatorType === AggregatorTypeEnum.WestSideEats) {
                return defer(() =>
                    postTerminalUpdateMutation(getHttpHeaders(deps.getState()), {
                        id: terminalId,
                        locationId: locationId,
                        isActive: true,
                        name: terminalName,
                        kioskCustomerInstructions: [],
                        type: TerminalType.Aggregator,
                        aggregatorConfig: {
                            type: aggregatorType,
                        },
                        auditData: constructAuditData(deps, true),
                    })
                ).pipe(
                    switchMap(() => {
                        Router.push("/locations/edit/aggregator/" + action.payload.terminalId)
                        return [integrateAggregatorSuccess(locationId, terminalId)]
                    })
                )
            } else {
                return EMPTY
            }
        })
    )

export const aggregatorUberStoresEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_UBER_STORES_REQUESTED),
        switchMap((action: Action) => {
            logger.info("aggregatorUberStoresEpic - requested", {
                data: action.payload,
            })

            const { code, serviceId } = action.payload
            return defer(() => {
                if (serviceId) {
                    return fetchUberStoresQuery(getHttpHeaders(deps.getState()), {
                        serviceId: serviceId,
                    })
                }
                return fetchUberStoresQueryByCode(getHttpHeaders(deps.getState()), {
                    code: code,
                })
            }).pipe(
                switchMap(response => {
                    const item = getEditServiceListItem(deps.getState())
                    const terminalId = getEditTerminalId(deps.getState())
                    const location = getLocationDataByTerminalId(deps.getState(), terminalId)

                    if (response.stores.length === 1 && (item || (location && terminalId))) {
                        return [
                            aggregatorUberStoresSuccess(response),
                            aggregatorUberStoreSelected(
                                response.uberAuth,
                                response.stores[0].storeId,
                                item?.locationId || (location?.id as string),
                                item?.id || (terminalId as string)
                            ),
                        ]
                    } else {
                        return [aggregatorUberStoresSuccess(response)]
                    }
                }),
                catchError(error => of(aggregatorUberStoresError(error)))
            )
        })
    )

export const aggregatorFoodmeowStoresEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_FOODMEOW_STORES_REQUESTED),
        switchMap((action: Action) => {
            logger.info("aggregatorFoodmeowStoresEpic - requested", {
                data: action.payload,
            })

            const code = action.payload.code
            return defer(() =>
                fetchFoodmeowStoresQuery(getHttpHeaders(deps.getState()), {
                    code: code,
                })
            ).pipe(
                switchMap(response => {
                    const item = getEditServiceListItem(deps.getState())

                    if (response.stores.length === 1 && item) {
                        return [
                            aggregatorFoodmeowStoresSuccess(response),
                            aggregatorFoodmeowStoreSelected(code, response.stores[0].storeId, item.locationId, item.id),
                        ]
                    } else {
                        return [aggregatorFoodmeowStoresSuccess(response)]
                    }
                }),
                catchError(error => of(aggregatorFoodmeowStoresError(error)))
            )
        })
    )

export const aggregatorUberStoreLinkEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_UBER_STORE_SELECTED),
        switchMap((action: Action) => {
            logger.info("aggregatorUberStoreLinkEpic - selected", {
                data: action.payload,
            })

            return defer(() => {
                if (!action.payload.auth) {
                    const accountId = getSelectedAccountId(deps.getState())
                    const location = getLocationDataByTerminalId(deps.getState(), action.payload.terminalId)
                    return requestEvent(
                        getHttpHeaders(deps.getState()),
                        createEventRequest(
                            EventType.UberMerchantAuthorizationRequested,
                            {
                                accountId: accountId as string,
                                locationId: location?.id as string,
                                terminalId: action.payload.terminalId,
                                correlationId: deps.idgen.next(),
                            },
                            {
                                uberStoreId: action.payload.storeId,
                                serviceId: action.payload.terminalId,
                            }
                        )
                    )
                }
                return linkUberStore(getHttpHeaders(deps.getState()), action.payload)
            }).pipe(
                switchMap(() => [integrateAggregatorSuccess(action.payload.locationId, action.payload.terminalId)]),
                catchError(() => {
                    openNotification(
                        NotificationType.Error,
                        "Integration failed",
                        "There was an error in contacting Uber Eats. You may have to try integrating again."
                    )
                    return EMPTY
                })
            )
        })
    )

export const aggregatorFoodmeowStoreLinkEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGGREGATOR_FOODMEOW_STORE_SELECTED),
        switchMap((action: Action) => {
            logger.info("aggregatorFoodmeowStoreLinkEpic - selected", {
                data: action.payload,
            })

            return defer(() => linkFoodmeowStore(getHttpHeaders(deps.getState()), action.payload)).pipe(
                switchMap(() => [integrateAggregatorSuccess(action.payload.locationId, action.payload.terminalId)]),
                catchError(() => {
                    openNotification(
                        NotificationType.Error,
                        "Integration failed",
                        "There was an error in contacting Foodmeow. You may have to try integrating again."
                    )
                    return EMPTY
                })
            )
        })
    )
export const aggregatorEditFormSubmitEpic = (
    action$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    action$.pipe(
        ofType(AGGREGATOR_EDIT_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("aggregatorEditFormSubmitEpic - form submitted", {
                data: action.payload,
            })

            const serviceDetail = getServiceDetailData(deps.getState())
            return [
                terminalUpdated({
                    ...serviceDetail,
                    id: action.payload.id,
                    isActive: true,
                    type: TerminalType.Aggregator,
                    locationId: action.payload.locationId,
                    name: action.payload.name,
                    aggregatorConfig: action.payload.aggregatorConfig,
                    metadata: {
                        onlineOrdering: {
                            priceMultiplier: constructPriceMultiplierValue(action.payload.priceMultiplier),
                            shouldRoundUpPrices: action.payload.shouldRoundUpPrices,
                        },
                    },
                }),
            ]
        })
    )

export default [
    fetchAggregatorServiceDetailRequested,
    fetchLocationsDetailRequested,
    fetchLocationsListRequested,
    clearStatusStateEpic,
    terminalCreatedEpics,
    terminalUpdatedEpics,
    deleteLocationEpics,
    locationUpdatedEpics,
    locationCreatedEpics,
    fetchLocationsRequested,
    deleteLocationClickedEpics,
    locationFormSubmittedEpics,
    deleteTerminalClickedEpics,
    closeLocationsPopoverRouterEpic,
    onIntegrate3rdPartRequestedEpic,
    aggregatorCreateFormEpic,
    aggregatorIntegrateFormEpic,
    aggregatorIntegrationRequestedEpic,
    aggregatorUberStoresEpic,
    aggregatorFoodmeowStoresEpic,
    aggregatorUberStoreLinkEpic,
    aggregatorFoodmeowStoreLinkEpic,
    aggregatorEditFormSubmitEpic,
    deleteAggregatorClickedEpics,
]
